import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from '@actual-app/components/button';
import { Input } from '@actual-app/components/input';
import { Text } from '@actual-app/components/text';
import { theme } from '@actual-app/components/theme';
import { View } from '@actual-app/components/view';
import {
  addDays,
  anonymise,
  earliestFunding,
  netPerExtraHour,
  numbersIn,
  restoreNames,
  simulate,
  unverifiedAmounts,
} from '@actual-app/core/shared/mtxbudget';
import type { Entity } from '@actual-app/core/shared/mtxbudget';

import { askAi } from './ai';
import { NotConfigured } from './QuinzainePage';
import { MtxPage, Section, useMoney } from './ui';
import { useMtxbudget } from './useMtxbudget';

type Message = { from: 'user' | 'tool'; text: string; warning?: string };

const UPCOMING_DAYS = 31;
const dollars = (cents: number) => Math.round(cents) / 100;

export function AssistantPage() {
  const { t } = useTranslation();
  const money = useMoney();
  const {
    config,
    plan,
    open,
    debts,
    monthlyMargin,
    today,
    occurrences,
    available,
    isLoading,
  } = useMtxbudget();
  const [messages, setMessages] = useState<Message[]>([]);
  const [question, setQuestion] = useState('');
  const [busy, setBusy] = useState(false);

  if (!config) {
    return (
      <MtxPage title={t('Assistant')}>
        {!isLoading && <NotConfigured />}
      </MtxPage>
    );
  }

  // Everything the engine is told comes from the calculation, under neutral
  // labels; nothing it answers is shown before its amounts are checked.
  function buildFacts() {
    if (!config) {
      return { facts: {}, entities: [] as Entity[] };
    }
    const outcome = simulate({
      startMonth: today.slice(0, 7),
      debts,
      projects: config.projects,
      monthlyMargin,
      savings: config.savings,
      emergencyFundTarget: config.emergencyFundTarget,
    });
    // The free margin alone can be slower than the regular payments already
    // planned: the earliest date is never later than the planned one.
    const earliest = (amount: number, plannedMonth: string | null) => {
      const date = earliestFunding(plan, amount, monthlyMargin);
      return date !== null &&
        plannedMonth !== null &&
        date.slice(0, 7) > plannedMonth
        ? plannedMonth
        : (date ?? plannedMonth);
    };
    // Up to the end of the plan, so that a charge the plan counts is one
    // the engine can see.
    const planEnd = plan.length > 0 ? plan[plan.length - 1].nextPayday : today;
    const upcoming = addDays(today, UPCOMING_DAYS);
    const horizon = planEnd > upcoming ? planEnd : upcoming;
    const charges = occurrences.filter(o => o.amount < 0 && o.date <= horizon);
    const chargeNames = [...new Set(charges.map(charge => charge.name))];
    const chargeLabel = (name: string) =>
      `échéance ${chargeNames.indexOf(name) + 1}`;

    const entities: Entity[] = [
      ...debts.map((debt, index) => ({
        label: `dette ${index + 1}`,
        name: debt.name,
      })),
      ...config.projects.map((project, index) => ({
        label: `projet ${index + 1}`,
        name: project.name,
      })),
      ...chargeNames.map(name => ({ label: chargeLabel(name), name })),
    ];

    const planned = open
      ? (config.pays[open.payday]?.hours ?? [
          config.pay.minHoursPerWeek,
          config.pay.minHoursPerWeek,
        ])
      : null;

    const facts = {
      aujourdhui: today,
      argentEnCompte: dollars(available),
      paies: plan.map(fortnight => ({
        date: fortnight.payday,
        heuresTravailleesDu: fortnight.periodStart,
        heuresTravailleesAu: fortnight.periodEnd,
        netEstime: dollars(fortnight.net),
        echeancesACouvrir: dollars(fortnight.totalCharges),
        margeLibreDesCetteDate: dollars(fortnight.margin),
        heuresMinimalesParSemainePourCouvrirLesCharges:
          fortnight.thresholdHoursPerWeek,
      })),
      semaineEnCours: open
        ? {
            compteDansLaPaieDu: open.payday,
            heuresMinimalesPourCouvrirLesCharges: open.thresholdHoursPerWeek,
            margeLibreAuxHeuresPrevues: dollars(open.margin),
          }
        : null,
      heures: {
        garantiesParSemaine: config.pay.minHoursPerWeek,
        prevuesParSemaine: planned ? (planned[0] + planned[1]) / 2 : null,
        limiteDesHeuresNormalesParSemaine: config.pay.regularHoursPerWeek,
        gainNetParHeureNormaleEnPlus: dollars(
          netPerExtraHour(config.pay, config.pay.minHoursPerWeek),
        ),
        gainNetParHeureSupplementaire: dollars(
          netPerExtraHour(config.pay, config.pay.regularHoursPerWeek),
        ),
      },
      margeMensuelleMoyenneAuxHeuresGaranties: dollars(monthlyMargin),
      ordreDeRemboursementPrevu: "la dette au taux le plus élevé d'abord",
      dettes: debts.map((debt, index) => ({
        libelle: `dette ${index + 1}`,
        solde: dollars(debt.balance),
        tauxAnnuelEnPourcent: debt.rate,
        paiementMensuelMinimum: dollars(debt.monthlyPayment),
        remboursableEnEntierAuPlusTotLe: earliest(
          debt.balance,
          outcome.debts.find(result => result.id === debt.id)?.paidOff ?? null,
        ),
        soldeeSiOnSuitLOrdrePrevu:
          outcome.debts.find(result => result.id === debt.id)?.paidOff ?? null,
      })),
      projets: config.projects.map((project, index) => ({
        libelle: `projet ${index + 1}`,
        montant: dollars(project.amount),
        pourLe: project.wished ?? null,
        finançableAuPlusTotLe: earliest(
          project.amount,
          outcome.projects.find(result => result.id === project.id)?.funded ??
            null,
        ),
        financeSiOnSuitLOrdrePrevu:
          outcome.projects.find(result => result.id === project.id)?.funded ??
          null,
      })),
      echeancesDesProchainsJours: charges.map(charge => ({
        libelle: chargeLabel(charge.name),
        date: charge.date,
        montant: dollars(-charge.amount),
      })),
    };
    return { facts, entities };
  }

  async function ask(text: string) {
    const asked = text.trim();
    if (asked === '' || busy) {
      return;
    }
    setQuestion('');
    setBusy(true);
    setMessages(list => [...list, { from: 'user', text: asked }]);

    const { facts, entities } = buildFacts();
    const result = await askAi<{ answer: string }>(
      'answer',
      { question: anonymise(asked, entities), faits: facts },
      ['answer'],
    );

    if (!result || typeof result.answer !== 'string') {
      setMessages(list => [
        ...list,
        {
          from: 'tool',
          text: t(
            "Le moteur d'IA ne répond pas pour l'instant. Les écrans « Ma quinzaine » et « Dettes et projets » donnent les mêmes chiffres.",
          ),
        },
      ]);
      setBusy(false);
      return;
    }

    const doubtful = unverifiedAmounts(result.answer, numbersIn(facts));
    setMessages(list => [
      ...list,
      {
        from: 'tool',
        text: restoreNames(result.answer, entities),
        warning:
          doubtful.length > 0
            ? t(
                'Montant(s) non vérifié(s) par le calcul : {{amounts}}. Ne vous y fiez pas sans contrôle.',
                {
                  amounts: doubtful
                    .map(amount => money(amount * 100))
                    .join(', '),
                },
              )
            : undefined,
      },
    ]);
    setBusy(false);
  }

  const suggestions = [
    t("Combien d'heures dois-je faire cette semaine ?"),
    t('Que dois-je payer avant la prochaine paie ?'),
    t('Quelle dette rembourser en premier ?'),
  ];

  return (
    <MtxPage title={t('Assistant')}>
      <View style={{ gap: 12, paddingBottom: 20 }}>
        {messages.length === 0 && (
          <Section style={{ padding: 16 }}>
            <Text style={{ color: theme.pageTextSubdued }}>
              {t(
                "Posez votre question comme vous le feriez à quelqu'un. Les chiffres viennent de vos comptes et du calcul ; chaque montant cité par l'IA est vérifié avant d'être affiché.",
              )}
            </Text>
          </Section>
        )}

        <View role="log" aria-live="polite" style={{ gap: 12 }}>
          {messages.map((message, index) => (
            <View
              key={index}
              style={{
                alignSelf: message.from === 'user' ? 'flex-end' : 'flex-start',
                maxWidth: '88%',
                padding: '10px 14px',
                gap: 6,
                borderRadius: 12,
                backgroundColor:
                  message.from === 'user'
                    ? theme.buttonPrimaryBackground
                    : theme.tableBackground,
                border:
                  message.from === 'user'
                    ? undefined
                    : `1px solid ${theme.tableBorder}`,
              }}
            >
              <Text
                style={{
                  color:
                    message.from === 'user'
                      ? theme.buttonPrimaryText
                      : undefined,
                }}
              >
                {message.text}
              </Text>
              {message.warning && (
                <Text style={{ fontSize: 12, color: theme.warningText }}>
                  {message.warning}
                </Text>
              )}
            </View>
          ))}
          {busy && (
            <Text style={{ color: theme.pageTextSubdued }}>
              {t('Calcul et rédaction en cours…')}
            </Text>
          )}
        </View>

        <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 8 }}>
          {suggestions.map(suggestion => (
            <Button
              key={suggestion}
              isDisabled={busy}
              onPress={() => void ask(suggestion)}
              // A long question goes to the line on a narrow phone.
              style={{
                minHeight: 44,
                maxWidth: '100%',
                borderRadius: 22,
                whiteSpace: 'normal',
                textAlign: 'center',
              }}
            >
              {suggestion}
            </Button>
          ))}
        </View>

        <View style={{ flexDirection: 'row', gap: 8 }}>
          <Input
            aria-label={t('Votre question')}
            placeholder={t('Posez une question sur votre budget')}
            value={question}
            onChangeValue={setQuestion}
            onEnter={value => void ask(value)}
            style={{ flex: 1, minWidth: 0, minHeight: 44 }}
          />
          <Button
            variant="primary"
            isDisabled={busy || question.trim() === ''}
            onPress={() => void ask(question)}
            style={{ minHeight: 44 }}
          >
            {busy ? t('Calcul…') : t('Envoyer')}
          </Button>
        </View>
      </View>
    </MtxPage>
  );
}
